<template>
    <div class="cl-page">
        <!-- Header -->
        <div class="cl-header">
            <h4 class="cl-title">{{ $t('components') }}</h4>
            <div class="cl-header-right">
                <b-input-group size="sm" class="cl-search">
                    <b-input-group-prepend is-text>
                        <b-icon icon="search" font-scale="0.85"></b-icon>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model="searchQuery"
                        :placeholder="$t('searchByLabelOrScope')"
                        @input="filterComponents"
                    ></b-form-input>
                    <b-input-group-append v-if="searchQuery">
                        <b-button variant="outline-secondary" @click="clearSearch">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
                <router-link to="/admin/components/create" class="btn btn-primary btn-sm cl-create-btn">
                    <b-icon icon="plus" class="mr-1"></b-icon>{{ $t('create') }}
                </router-link>
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="cl-loading">
            <b-spinner></b-spinner>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredItems.length === 0 && !searchQuery" class="cl-empty">
            <b-icon icon="box-seam" font-scale="2.5" class="mb-3"></b-icon>
            <p>No components found</p>
        </div>

        <!-- No Search Results -->
        <div v-else-if="filteredItems.length === 0 && searchQuery" class="cl-empty">
            <b-icon icon="search" font-scale="2" class="mb-3"></b-icon>
            <p>No components matching "{{ searchQuery }}"</p>
            <b-button variant="outline-primary" size="sm" @click="clearSearch">Clear search</b-button>
        </div>

        <!-- Component List -->
        <div v-else class="cl-list">
            <div class="cl-list-header">
                <span class="cl-col-name">{{ $t('label') }}</span>
                <span class="cl-col-scope">{{ $t('scope') }}</span>
                <span class="cl-col-status">{{ $t('status') }}</span>
                <span class="cl-col-version">Dev</span>
                <span class="cl-col-version">Live</span>
                <span class="cl-col-updated">{{ $t('updatedAt') }}</span>
            </div>
            <div
                v-for="item in paginatedItems"
                :key="item.uuid || item.name"
                class="cl-row"
                @click="rowClicked(item)"
            >
                <div class="cl-col-name">
                    <span class="cl-dot" :class="item.is_active ? 'cl-dot--active' : 'cl-dot--inactive'"></span>
                    <span class="cl-name">{{ $translateLabel(item.label, item.name) }}</span>
                </div>
                <div class="cl-col-scope">
                    <span v-if="item.scope" class="cl-scope-chip">{{ item.scope }}</span>
                    <span v-else class="text-muted">&mdash;</span>
                </div>
                <div class="cl-col-status">
                    <span v-if="item.is_public" class="cl-status-chip cl-status-chip--public">
                        <b-icon icon="globe2" font-scale="0.85" class="mr-1"></b-icon>public
                    </span>
                    <span v-else class="cl-status-chip cl-status-chip--private">
                        <b-icon icon="lock" font-scale="0.85" class="mr-1"></b-icon>private
                    </span>
                </div>
                <div class="cl-col-version">
                    <span v-if="item.dev_version" class="cl-version-chip cl-version-chip--dev">{{ item.dev_version }}</span>
                    <span v-else class="text-muted">&mdash;</span>
                </div>
                <div class="cl-col-version">
                    <span v-if="item.public_version" class="cl-version-chip cl-version-chip--live">{{ item.public_version }}</span>
                    <span v-else class="text-muted">&mdash;</span>
                </div>
                <div class="cl-col-updated">
                    <span class="text-muted">{{ $toUserTimeZone(item.UpdatedAt) }}</span>
                </div>
            </div>

            <!-- Pagination -->
            <div class="cl-pagination" v-if="filteredItems.length > perPage">
                <small class="text-muted mr-3">{{ filteredItems.length }} components</small>
                <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredItems.length"
                    :per-page="perPage"
                    size="sm"
                    class="mb-0"
                ></b-pagination>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ComponentsList',
    data() {
        return {
            currentPage: 1,
            perPage: 20,
            items: [],
            searchQuery: '',
            filteredItems: [],
            loading: true
        }
    },
    computed: {
        paginatedItems() {
            const start = (this.currentPage - 1) * this.perPage
            return this.filteredItems.slice(start, start + this.perPage)
        }
    },
    methods: {
        rowClicked(component) {
            let ref = component.name
            if (component.scope) {
                ref = `${component.scope}/${ref}`
            }
            this.$router.push({ path: `/admin/component/${ref}/settings` })
        },

        filterComponents() {
            this.currentPage = 1
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                this.filteredItems = this.items
                return
            }

            const query = this.searchQuery.toLowerCase().trim()

            this.filteredItems = this.items.filter(item => {
                const translatedLabel = this.$translateLabel(item.label, item.name)
                const label = translatedLabel ? translatedLabel.toLowerCase() : ''
                const scope = item.scope ? item.scope.toLowerCase() : ''
                const name = item.name ? item.name.toLowerCase() : ''

                return label.includes(query) ||
                       scope.includes(query) ||
                       name.includes(query)
            })
        },

        clearSearch() {
            this.searchQuery = ''
            this.filteredItems = this.items
            this.currentPage = 1
        }
    },

    mounted() {
        this.$store.dispatch("fetchComponents").then(data => {
            this.items = data.filter(d => {
                return this.$can("settings " + d.getRef()) || this.$isAdmin()
            })
            this.filteredItems = this.items

            if (this.items.length < 1) {
                this.$router.back()
            }
        }).catch(error => {
            if (error.response !== undefined) {
                if (error.response.status == 500) {
                    this.$serverErrorModal(error)
                    return
                }
            }
            this.$debugLog(error)
        }).finally(() => {
            this.loading = false
        })
    }
}
</script>

<style scoped>
/* ── Page ───────────────────────────────────────── */
.cl-page {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 120px);
}

/* ── Header ─────────────────────────────────────── */
.cl-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0 14px;
    border-bottom: 1px solid var(--border-default);
    margin-bottom: 16px;
    gap: 16px;
    flex-wrap: wrap;
}

.cl-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
    white-space: nowrap;
}

.cl-header-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: flex-end;
}

.cl-search {
    max-width: 320px;
}

.cl-search .input-group-text {
    background: transparent;
    border-right: 0;
    color: var(--text-muted);
}

.cl-search .form-control {
    border-left: 0;
}

.cl-create-btn {
    white-space: nowrap;
    font-weight: 500;
}

/* ── Loading / Empty ────────────────────────────── */
.cl-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 80px;
}

.cl-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: var(--text-muted);
    font-size: 14px;
}

/* ── List ───────────────────────────────────────── */
.cl-list-header {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 80px 80px 140px;
    gap: 12px;
    padding: 8px 14px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: var(--text-muted, #949ba4);
    border-bottom: 1px solid var(--border-default);
}

.cl-row {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 80px 80px 140px;
    gap: 12px;
    padding: 10px 14px;
    align-items: center;
    border-bottom: 1px solid var(--border-default);
    cursor: pointer;
    transition: background 0.12s;
    font-size: 13px;
}

.cl-row:hover {
    background: var(--bg-card-secondary);
}

.cl-row:last-child {
    border-bottom: none;
}

/* ── Columns ────────────────────────────────────── */
.cl-col-name {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.cl-name {
    font-weight: 500;
    color: var(--text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.cl-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.cl-dot--active {
    background: var(--success-start, #48bb78);
    box-shadow: 0 0 0 2px rgba(72, 187, 120, 0.2);
}

.cl-dot--inactive {
    background: var(--text-disabled, #6d7480);
}

.cl-col-scope,
.cl-col-status,
.cl-col-version,
.cl-col-updated {
    display: flex;
    align-items: center;
    min-width: 0;
}

.cl-col-updated {
    font-size: 12px;
}

/* ── Chips ──────────────────────────────────────── */
.cl-scope-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-secondary);
    border: 1px solid var(--border-default);
}

.cl-status-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.cl-status-chip--public {
    background: rgba(108, 128, 255, 0.10);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.2);
}

.cl-status-chip--private {
    background: var(--bg-card-secondary, rgba(255, 255, 255, 0.05));
    color: var(--text-muted, #949ba4);
    border: 1px solid var(--border-default);
}

.cl-version-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
}

.cl-version-chip--dev {
    background: rgba(72, 187, 120, 0.10);
    color: var(--success-start, #48bb78);
    border: 1px solid rgba(72, 187, 120, 0.2);
}

.cl-version-chip--live {
    background: rgba(108, 128, 255, 0.10);
    color: var(--primary-start, #6c80ff);
    border: 1px solid rgba(108, 128, 255, 0.2);
}

/* ── Pagination ─────────────────────────────────── */
.cl-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 12px 14px;
    border-top: 1px solid var(--border-default);
}

/* ── Responsive ─────────────────────────────────── */
@media (max-width: 768px) {
    .cl-list-header {
        display: none;
    }

    .cl-row {
        grid-template-columns: 1fr;
        gap: 6px;
        padding: 12px 14px;
    }

    .cl-col-updated {
        display: none;
    }
}
</style>
